import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Roots and the Pythagorean theorem.
 *
 * Square roots and the theorem belong together: the theorem is what makes roots
 * unavoidable, and roots are what make the theorem's answers writable. Between
 * them they carry the moment a student learns that some quantities cannot be
 * written down exactly — which is a genuinely unsettling idea and is treated as
 * one here. Cube roots sit between the two because they are where the *index*
 * of a root becomes something to read rather than assume, and because the edge
 * of a cube is the first question whose answer a square root cannot give.
 */
export const ROOT_LESSONS: Lesson[] = [
  lesson({
    id: 'square-roots-01',
    unitId: 'unit-roots',
    conceptId: 'square-roots',
    title: 'Square Roots and Irrational Numbers',
    summary: 'Undoing a square — and discovering that most of the answers cannot be written as fractions.',
    objectives: [
      'Find exact square roots of perfect squares',
      'Estimate a root by bracketing it between whole numbers',
      'Explain what makes a number irrational',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'The question a square asks backwards',
        blocks: [
          text('A square with sides of 7 has an area of 49. Now ask it the other way: a square has an area of 49 — how long are its sides?'),
          math('\\sqrt{49} = 7'),
          text('A **square root** undoes a square. That is all the symbol means, and for numbers like 49 it is easy. The interesting part is what happens for numbers like 50.'),
        ],
      },
      intuition: {
        title: 'The numbers that ran out',
        blocks: [
          text('There is no whole number whose square is 50. There is no fraction either — and that second claim is the surprising one, because until now every number you have met could be written as one integer over another.'),
          math('7^{2} = 49 \\qquad 7.07^{2} = 49.98 \\qquad 7.0710678^{2} = 49.9999998\\ldots'),
          insight('You can get as close as you like and never arrive. √50 has a decimal that never ends and never repeats, and no fraction equals it exactly. Such a number is called **irrational** — literally, not a ratio.'),
          text('This was genuinely shocking when it was discovered. The Pythagoreans believed every quantity was a ratio of whole numbers; the diagonal of a unit square is √2, and it is not. The numbers ran out before the geometry did.'),
          warning('A square root is not half. √36 is 6, because 6 × 6 = 36 — it is not 18. Halving and rooting agree only at 0 and 4, which is exactly often enough to let the mistake survive.', 'The commonest error'),
          text('For an irrational root, the useful skill is bracketing it between the perfect squares on either side:'),
          math('49 < 50 < 64 \\quad\\Longrightarrow\\quad 7 < \\sqrt{50} < 8'),
          insight('And one more rule, which matters enormously in the next lesson: a root does **not** split across addition. √(9 + 16) is √25 = 5, not 3 + 4 = 7.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['n', 'n²', 'so √n² is'], [
            ['1', '1', '1'],
            ['5', '25', '5'],
            ['9', '81', '9'],
            ['12', '144', '12'],
            ['20', '400', '20'],
          ]),
          text('Knowing the perfect squares up to about 400 by sight is worth the small effort: it turns most root questions into recall, and makes bracketing the irrational ones quick.'),
          figure(
            { kind: 'grid', width: 6, height: 6 },
            'Thirty-six unit squares in a 6 by 6 arrangement. The root of the area is the side length — which is what the word "square" is doing in "square root".',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find √196 exactly, and estimate √110 to the nearest whole number.'),
          steps([
            { label: 'For 196, look for the perfect square.', latex: '14 \\times 14 = 196' },
            { label: 'So the root is exact.', latex: '\\sqrt{196} = 14' },
            { label: 'For 110, bracket it.', latex: '100 < 110 < 121' },
            { label: 'Take roots throughout.', latex: '10 < \\sqrt{110} < 11' },
            { label: '110 is much nearer 121 than 100, so round up.', latex: '\\sqrt{110} \\approx 10.5' },
          ]),
          tip('Check any estimate by squaring it. 10.5² = 110.25, which is just above 110 — so the true value is a shade under 10.5, and you know your answer is good.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('square-roots', 4)] },
      independent: { title: 'On your own', blocks: [practice('square-roots', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('square-roots', 3, 'Roots of sums, and telling rational from irrational.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A square root undoes a square: √49 = 7 because 7 × 7 = 49.',
            'Rooting is not halving.',
            'A root does not split across addition — add first, then take the root.',
            'If the number is not a perfect square, its root is irrational: the decimal never ends and never repeats.',
            'Bracket an irrational root between the perfect squares on either side.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'cube-roots-01',
    unitId: 'unit-roots',
    conceptId: 'cube-roots',
    title: 'Cube Roots',
    summary: 'Undoing a cube — the operation that turns a volume back into a length, and the one root that keeps a minus sign.',
    objectives: [
      'Find the cube root of a perfect cube',
      'Use a cube root to recover the edge of a cube from its volume',
      'Explain why a cube root of a negative number exists when a square root of one does not',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'A volume asked backwards',
        blocks: [
          text('A cube with edges of 4 cm has a volume of 4 × 4 × 4 = 64 cubic cm. Now ask it the other way round: a cube holds 64 cubic cm — how long is each edge?'),
          math('\\sqrt[3]{64} = 4'),
          text('That small 3 is not decoration. It says how many equal factors are being undone: two for a square root, three for a cube root.'),
        ],
      },
      intuition: {
        title: 'The little number counts the factors',
        blocks: [
          text('A square root asks for the number that appears **twice**; a cube root asks for the number that appears **three times**. Same idea, one more factor.'),
          math('\\sqrt{64} = 8 \\text{ because } 8 \\times 8 = 64, \\qquad \\sqrt[3]{64} = 4 \\text{ because } 4 \\times 4 \\times 4 = 64'),
          warning('Those are two different questions about the same number, and 64 answers both — which is exactly why the index is worth reading before you start. If you see 8 where the answer is 4, you undid two factors instead of three.', 'Read the index'),
          text('And it is not a division. ∛125 is not 125 ÷ 3 ≈ 41.7; it is 5, because 5 × 5 × 5 = 125. Checking is easy and settles it in one line.'),
          text('**Now the part where cubes and squares part company.** No real number squares to a negative, because a negative times a negative is positive. But a negative *cubed* stays negative:'),
          math('(-5)^{3} = -5 \\times -5 \\times -5 = -125, \\qquad \\text{so } \\sqrt[3]{-125} = -5'),
          insight('Every real number has exactly one real cube root, positive or negative. Square roots are the fussy ones: positive numbers have two, and negatives have none at all. Three factors keep the sign; two destroy it.'),
        ],
      },
      visual: {
        title: 'The cubes worth knowing',
        blocks: [
          table(['n', 'n³', '∛n³'], [
            ['1', '1', '1'],
            ['2', '8', '2'],
            ['3', '27', '3'],
            ['4', '64', '4'],
            ['5', '125', '5'],
            ['6', '216', '6'],
            ['7', '343', '7'],
            ['8', '512', '8'],
            ['9', '729', '9'],
            ['10', '1000', '10'],
          ]),
          tip('The cubes thin out fast — there are only ten of them below a thousand, against thirty-one squares. That is what makes estimating a cube root easy: there are far fewer landmarks to sit between.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A cubical water tank holds 343 litres, and 1 litre fills 1000 cubic cm. How long is each edge, in centimetres?'),
          steps([
            { label: 'Convert to cubic centimetres.', latex: '343 \\times 1000 = 343{,}000\\text{ cm}^{3}' },
            { label: 'The edge is the cube root of the volume.', latex: 'e = \\sqrt[3]{343{,}000}' },
            { label: 'Split it into two cube roots you know.', latex: '\\sqrt[3]{343} \\times \\sqrt[3]{1000} = 7 \\times 10' },
            { label: 'So the edge is 70 cm.', latex: 'e = 70\\text{ cm}' },
          ]),
          text('Check it: 70 × 70 × 70 = 343,000. Every root question can be checked by putting the answer back, and with three factors to multiply it is still a ten-second job.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('cube-roots', 4)] },
      independent: { title: 'On your own', blocks: [practice('cube-roots', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('cube-roots', 3, 'Roots that fall between whole numbers, and negatives.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A cube root undoes three equal factors, as a square root undoes two.',
            'The small 3 on the sign is the whole difference — read it first.',
            'A cube root is not a division by three; check by multiplying back.',
            'Volume to edge is exactly what a cube root is for.',
            'Negative numbers have cube roots, because three negative factors stay negative.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'pythagoras-01',
    unitId: 'unit-roots',
    conceptId: 'pythagoras',
    title: 'The Pythagorean Theorem',
    summary: 'The relationship between the sides of a right triangle — and the first result you can prove rather than measure.',
    objectives: [
      'Find the hypotenuse of a right triangle',
      'Find a shorter side, by subtracting rather than adding',
      'Use the converse to test whether a triangle is right-angled',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A result, not a measurement',
        blocks: [
          text('Draw a right triangle with sides 3 and 4. Measure the long side and you get 5. Try 6 and 8, and you get 10. Try 5 and 12, and you get 13.'),
          text('This is not a coincidence, and it is not approximately true. For every right triangle that has ever existed or could exist:'),
          math('a^{2} + b^{2} = c^{2}'),
          text('where c is the **hypotenuse** — the side opposite the right angle, and always the longest.'),
        ],
      },
      intuition: {
        title: 'Why it is true',
        blocks: [
          text('The squares in the formula are literal squares. Build a square on each side of a right triangle: the two smaller ones, together, have exactly the same area as the big one.'),
          math('3^{2} + 4^{2} = 9 + 16 = 25 = 5^{2}'),
          insight('Here is a proof you can hold in your head. Take four copies of the triangle and arrange them inside a square of side (a + b), first leaving a hole of area c², then rearranged to leave two holes of areas a² and b². Same big square, same four triangles, so the leftover space must match: a² + b² = c².'),
          text('That argument is worth dwelling on, because it is different in kind from everything before it. Nobody measured anything. The result holds for triangles too large to draw and too small to see, and you know it does.'),
          warning('The formula finds the *hypotenuse* by adding. To find a shorter side you must subtract: a² = c² − b². Adding the two numbers you were given is the classic error, and it produces a triangle where the hypotenuse is not the longest side — which is impossible.', 'Which side is missing?'),
          text('The theorem also works backwards. If a² + b² = c² holds for three lengths, the triangle **must** be right-angled. That converse is how builders check a corner is square with nothing but a tape measure.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'shape', shape: 'triangle', labels: { base: 'b = 4', height: 'a = 3', hypotenuse: 'c = 5' } },
            'The small square marks the right angle. The hypotenuse is the side opposite it, and it is always the longest.',
          ),
          text('A few triples are worth recognising on sight, because they appear constantly in problems:'),
          table(['a', 'b', 'c', 'Check'], [
            ['3', '4', '5', '9 + 16 = 25'],
            ['5', '12', '13', '25 + 144 = 169'],
            ['8', '15', '17', '64 + 225 = 289'],
            ['7', '24', '25', '49 + 576 = 625'],
          ]),
          insight('Any multiple of a triple is also a triple: 3-4-5 scaled by 2 gives 6-8-10, by 3 gives 9-12-15. That is proportional reasoning from Grade 7 doing useful work.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A ladder 13 m long leans against a wall, with its foot 5 m from the base. How far up the wall does it reach?'),
          steps([
            { label: 'The ladder is the hypotenuse — it is opposite the right angle at the ground.', latex: 'c = 13,\\quad b = 5' },
            { label: 'A shorter side is missing, so rearrange.', latex: 'a^{2} = c^{2} - b^{2}' },
            { label: 'Substitute.', latex: 'a^{2} = 169 - 25 = 144' },
            { label: 'Take the square root.', latex: 'a = 12\\text{ m}' },
          ]),
          tip('Sanity-check the answer against the hypotenuse. 12 is less than 13, as it must be. Had you added and got √194 ≈ 13.9, the "shorter" side would be longer than the ladder — a result the picture immediately refutes.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('pythagoras', 4)] },
      independent: { title: 'On your own', blocks: [practice('pythagoras', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('pythagoras', 3, 'Finding a shorter side, and testing whether a triangle is right-angled.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'a² + b² = c², where c is the hypotenuse — the side opposite the right angle.',
            'The hypotenuse is always the longest side, which is how you check your answer.',
            'Add to find the hypotenuse; subtract to find a shorter side.',
            'The converse holds: if the numbers fit, the triangle is right-angled.',
            'It applies only to right triangles.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'coordinate-distance-01',
    unitId: 'unit-roots',
    conceptId: 'coordinate-distance',
    title: 'Distance on the Coordinate Plane',
    summary: 'The gap between two points is the hypotenuse of a triangle nobody drew — so the theorem measures it.',
    objectives: [
      'Find the distance between two points on a grid',
      'Draw the right triangle a pair of points implies',
      'Decide which of two points is nearer without measuring',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'A ruler you do not have',
        blocks: [
          text('Two points sit on a grid: (1, 2) and (5, 5). How far apart are they?'),
          text('Along a row it would be easy — count the squares. Diagonally there is nothing to count, because the line crosses the squares rather than following them.'),
          insight('So make the triangle. Go across, then up. Those two moves run along the grid and can be counted, and the direct route is the hypotenuse of the right triangle they form.'),
        ],
      },
      intuition: {
        title: 'Across, up, and the theorem',
        blocks: [
          text('The horizontal leg is the difference of the x-coordinates and the vertical leg is the difference of the y-coordinates. Both are lengths, so take them positively — which way you travelled does not change how far it is.'),
          math('a = |x_{2} - x_{1}| \\qquad b = |y_{2} - y_{1}| \\qquad d = \\sqrt{a^{2} + b^{2}}'),
          text('For (1, 2) and (5, 5) that gives legs of 4 and 3, and a distance of 5. It is the 3-4-5 triangle from the last lesson, arriving without being sent for.'),
          warning('The two legs are not added. Across 4 and up 3 is a journey of 7 along the grid lines, and the direct route is shorter than that — it has to be, or a straight line would not be the shortest path. Adding the legs is the classic error here, and it always gives too much.', 'The direct route is shorter'),
          insight('Nor is the answer the sum of the squares. 16 + 9 = 25 is an *area*; the distance is its square root, 5. Every distance question ends with a root, and most of the time that root is irrational — which is exactly why this waits until square roots exist.'),
          text('Written out in one line, this is the **distance formula** — and it is worth seeing that it is not a new fact:'),
          math('d = \\sqrt{(x_{2} - x_{1})^{2} + (y_{2} - y_{1})^{2}}'),
          tip('Squaring destroys any minus sign, so the order of the two points never matters. (5, 5) to (1, 2) is the same distance as (1, 2) to (5, 5), and the formula already knows it.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -2,
              max: 8,
              points: [
                { x: 1, y: 2, label: 'A (1, 2)' },
                { x: 5, y: 2 },
                { x: 5, y: 5, label: 'B (5, 5)' },
              ],
            },
            'Three points, and the right triangle they make. The two legs run along the grid and can be counted: 4 across and 3 up. The distance from A to B is the third side.',
          ),
          text('The corner point is not part of the question. It is scaffolding — you invent it, use it, and never mention it in the answer.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the distance between (−3, 4) and (2, −8).'),
          steps([
            { label: 'Find the horizontal leg.', latex: '|2 - (-3)| = 5' },
            { label: 'Find the vertical leg.', latex: '|-8 - 4| = 12' },
            { label: 'Square both and add.', latex: '5^{2} + 12^{2} = 25 + 144 = 169' },
            { label: 'Take the square root.', latex: 'd = \\sqrt{169} = 13' },
          ]),
          text('The negatives did no damage, because both differences were taken as lengths. Had the signs been kept and the legs added, the answer would have been −7 — a distance that is not merely wrong but impossible.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('coordinate-distance', 4)] },
      independent: { title: 'On your own', blocks: [practice('coordinate-distance', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('coordinate-distance', 3, 'Negative coordinates, and deciding which of two points is nearer.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Two points make a right triangle whose legs run along the grid.',
            'The legs are the differences of the x- and of the y-coordinates, taken positively.',
            'Distance is the hypotenuse: square both legs, add, then take the root.',
            'Adding the legs gives the journey along the grid, which is always too far.',
            'The sum of the squares is not the answer until it has been rooted.',
          ]),
        ],
      },
    },
  }),
];
